import React, { useEffect, useRef } from 'react';
import { safeCleanupNodes, isNodeInDOM } from '../../utils/domUtils';

/**
 * Higher-order component that provides safe DOM manipulation
 * Wraps components that might have DOM manipulation issues
 */
const SafeDOMWrapper = ({ children, onError }) => {
    const containerRef = useRef(null);
    const cleanupNodesRef = useRef([]);

    // Override removeChild to be safe
    useEffect(() => {
        const originalRemoveChild = Node.prototype.removeChild;
        
        Node.prototype.removeChild = function(child) {
            try {
                // Check if child is actually a child of this node
                if (!this.contains(child)) {
                    console.warn('SafeDOMWrapper: Attempted to remove child that is not a child of parent');
                    if (onError) {
                        onError(new Error('Attempted to remove child that is not a child of parent'));
                    }
                    return child; // Return the child as if removal was successful
                }
                
                return originalRemoveChild.call(this, child);
            } catch (error) {
                console.error('SafeDOMWrapper: Error in removeChild:', error);
                if (onError) {
                    onError(error);
                }
                return child; // Return the child as if removal was successful
            }
        };

        // Cleanup on unmount
        return () => {
            // Restore original removeChild
            Node.prototype.removeChild = originalRemoveChild;
            
            // Clean up any tracked nodes
            safeCleanupNodes(cleanupNodesRef.current);
        };
    }, [onError]);

    // Track nodes created within this wrapper
    const trackNode = (node) => {
        if (node && isNodeInDOM(node)) {
            cleanupNodesRef.current.push(node);
        }
    };

    // Provide safe DOM methods to children
    const safeDOMMethods = {
        trackNode,
        safeRemoveChild: (parent, child) => {
            const result = parent.removeChild(child);
            unregisterNode(child);
            return result;
        },
        safeRemove: (node) => {
            const result = node.remove();
            unregisterNode(node);
            return result;
        }
    };

    const unregisterNode = (node) => {
        cleanupNodesRef.current = cleanupNodesRef.current.filter(n => n !== node);
    };

    return (
        <div ref={containerRef} data-safe-dom-wrapper="true">
            {React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                        ...child.props,
                        safeDOM: safeDOMMethods
                    });
                }
                return child;
            })}
        </div>
    );
};

export default SafeDOMWrapper;
